import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { DrawerTx } from './screens/transaction-form'
import { useActiveOrg } from './app-state/active-org'

type AppState = {
  railSelected: string
  setRailSelected: (id: string) => void
  cmdkOpen: boolean
  setCmdkOpen: (open: boolean) => void
  drawerTx: DrawerTx | null
  setDrawerTx: (tx: DrawerTx | null) => void
  toast: string | null
  setToast: (msg: string | null) => void
  search: string
  setSearch: (q: string) => void
  openNewTx: () => void
}

const AppCtx = createContext<AppState | null>(null)

export function AppStateProvider({ children }: { children: ReactNode }) {
  const { activeAccountId, setActiveAccountId, activeOrgId } = useActiveOrg()
  const railSelected = activeAccountId
  const setRailSelected = useCallback(
    (id: string) => setActiveAccountId(id as string | 'all'),
    [setActiveAccountId],
  )

  const [cmdkOpen, setCmdkOpen] = useState(false)
  const [drawerTx, setDrawerTx] = useState<DrawerTx | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const openNewTx = useCallback(() => {
    // When the sidebar is scoped to a specific account, pre-fill it (and its org)
    // on the new-operation form so the created op lands on the account in view.
    if (activeAccountId !== 'all') {
      setDrawerTx({
        mode: 'new',
        defaults: { accountId: activeAccountId, organizationId: activeOrgId },
      })
    } else {
      setDrawerTx({ mode: 'new' })
    }
  }, [activeAccountId, activeOrgId])

  const value = useMemo<AppState>(
    () => ({
      railSelected,
      setRailSelected,
      cmdkOpen,
      setCmdkOpen,
      drawerTx,
      setDrawerTx,
      toast,
      setToast,
      search,
      setSearch,
      openNewTx,
    }),
    [railSelected, setRailSelected, cmdkOpen, drawerTx, toast, search, openNewTx],
  )

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>
}

export function useAppState(): AppState {
  const ctx = useContext(AppCtx)
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider')
  return ctx
}
